import { animate, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, JsonPipe } from '@angular/common';
import {
  Component,
  computed,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { NGXLogger } from 'ngx-logger';
import { Observable, of, Subject, take, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { ProtestantCanonBooks } from '../../../models/books.bible.model';
import { Member } from '../../../models/member.model';
import {
  QuestionType,
  QuizQuestion,
} from '../../../models/question.quiz.model';
import { Quiz, QuizStatus } from '../../../models/quiz.model';
import { UserModel } from '../../../models/user.model';
import { QuizService } from '../../../services/quiz.service';
import { UtilsService } from '../../../services/utils.service';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnFabComponent } from '../../components/buttons/btn-fab/btn-fab.component';
import { QuestionSearchComponent } from '../question-search/question-search.component';
import {
  ChipColor,
  ChipComponent,
} from '../../components/inputs/chip/chip.component';
import { TooltipComponent } from '../../components/tooltips/tooltip/tooltip.component';

@Component({
  selector: 'app-quiz-edit',
  standalone: true,
  imports: [
    BtnBackComponent,
    ChipComponent,
    ReactiveFormsModule,
    AsyncPipe,
    JsonPipe,
    BtnFabComponent,
    BtnActionComponent,
    TooltipComponent,
    QuestionSearchComponent,
  ],
  templateUrl: './quiz-edit.component.html',
  animations: [
    // Animation for the Backdrop (Fade)
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
    ]),
    // Animation for the Panel (Slide)
    trigger('slideInOut', [
      transition(
        ':enter',
        [
          // Start state: if mobile, below screen; if desktop, off-right
          style({ transform: '{{startPos}}' }),
          animate(
            '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
            style({ transform: 'translate(0,0)' }),
          ),
        ],
        { params: { startPos: 'translateX(100%)' } },
      ), // Default param
      transition(
        ':leave',
        [animate('250ms ease-in', style({ transform: '{{startPos}}' }))],
        { params: { startPos: 'translateX(100%)' } },
      ),
    ]),
  ],
  styleUrl: './quiz-edit.component.css',
})
export class QuizEditComponent implements OnInit, OnDestroy {
  showSearch = false;
  isMobile = false;

  store: Store<AppState> = inject(Store<AppState>);
  router: Router = inject(Router);
  fb: FormBuilder = inject(FormBuilder);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private logger: NGXLogger = inject(NGXLogger);
  private quizService = inject(QuizService);
  private utilsService = inject(UtilsService);

  groupId: string | null = this.activatedRoute.snapshot.paramMap.get('id')!;
  quizId: string | null = this.activatedRoute.snapshot.paramMap.get('quizId')!;

  user: UserModel | null = null;
  quiz: Quiz | null = {} as Quiz;
  students$: Observable<Member[]> = of([]);
  session$: Observable<any> = of(null);

  selectedQuestions = signal<QuizQuestion[]>([]);
  addQuestions: string[] = [];
  searchedQuestions$: Observable<QuizQuestion[]> = of([]);

  isLoading = signal(false);
  isWorking = signal(false);
  quizForm: FormGroup;

  chipColor = ChipColor;
  protestantBooks = ProtestantCanonBooks;
  questionTypes = Object.values(QuestionType);

  totalScore = computed(() => {
    return this.selectedQuestions().reduce((acc, q) => acc + (q.score || 0), 0);
  });

  get isTimeLimitEnabled(): boolean {
    const val = this.quizForm.get('enableTimeLimit')?.value;
    return val === true || val === 'true';
  }

  myRoles$: Observable<any> = new Observable();
  exit$ = new Subject<void>();

  constructor() {
    this.quizForm = this.fb.group({
      title: [null, [Validators.required]],
      startDate: [dayjs().format('YYYY-MM-DD'), [Validators.required]],
      endDate: [
        dayjs().add(3, 'day').format('YYYY-MM-DD'),
        [Validators.required],
      ],
      totalDuration: [30, [Validators.required, Validators.min(0)]],
      byInvite: [false, [Validators.required]],
      groupId: [this.groupId, [Validators.required]],
      createdById: [null, [Validators.required]],
      createdByName: [null, [Validators.required]],
      enableScoring: [true, [Validators.required]],
      allowMultipleAttempts: [true, [Validators.required]],
      shortAnswerPercentage: [50, [Validators.required, Validators.min(0), Validators.max(100)]],
      enableTimeLimit: [true, [Validators.required]],
    });
  }

  onSearchResults(questions: QuizQuestion[]) {
    this.searchedQuestions$ = of(questions);
  }

  onSearchCleared() {
    this.searchedQuestions$ = of([]);
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  ngOnInit() {
    this.checkScreenSize();

    this.activatedRoute.paramMap.pipe(take(1)).subscribe((params) => {
      this.groupId = params.get('id');
      this.quizId = params.get('quizId');
      if (this.quizId) {
        this.loadQuizDetails();
      }
    });

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
        this.quizForm.patchValue({
          createdById: this.user?.uid,
          createdByName: this.user?.name,
        });
      });

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.quizForm.get('enableTimeLimit')?.valueChanges
      .pipe(takeUntil(this.exit$))
      .subscribe(enable => {
        const totalDurationControl = this.quizForm.get('totalDuration');
        if (enable === true || enable === 'true') {
          totalDurationControl?.enable();
          totalDurationControl?.setValidators([Validators.required, Validators.min(0)]);
        } else {
          totalDurationControl?.disable();
          totalDurationControl?.clearValidators();
        }
        totalDurationControl?.updateValueAndValidity();
      });
  }

  loadQuizDetails() {
    if (!this.groupId || !this.quizId) {
      this.logger.error('Group ID or Quiz ID is missing in route parameters.');
      return;
    }
    this.quizService
      .getQuizById({ groupId: this.groupId, quizId: this.quizId })
      .subscribe({
        next: (quiz) => {
          this.quiz = quiz;

          this.quizForm.patchValue({
            title: quiz?.title,
            startDate: quiz?.scheduledAt
              ? dayjs(quiz.scheduledAt.toDate()).format('YYYY-MM-DD')
              : null,
            endDate: quiz?.endDate
              ? dayjs(quiz.endDate.toDate()).format('YYYY-MM-DD')
              : null,
            totalDuration: quiz?.totalDuration,
            byInvite: !!quiz?.inviteCode,
            enableScoring: quiz?.enableScoring !== false,
            allowMultipleAttempts: quiz?.allowMultipleAttempts !== false,
            shortAnswerPercentage: quiz?.shortAnswerPercentage ?? 50,
            enableTimeLimit: quiz?.enableTimeLimit !== false,
          });

          this.myRoles$.pipe(take(1)).subscribe((roles) => {
            if (
              (roles.faculty && this.quiz?.createdById === this.user?.uid) ||
              roles.management
            ) {
              this.loadQuestions();
            }
          });
        },
        error: (err) => {
          this.logger.error('Error loading quiz details: ', err);
          this.utilsService.onShowAlert(
            {
              code: 'LOAD_QUIZ_ERROR',
              title: 'Error Loading Quiz',
              message: 'Failed to load quiz details. Please try again later.',
            } as AppExceptionModel,
            false,
          );
        },
      });
  }

  async loadQuestions() {
    if (!this.groupId || !this.quiz?.questions?.length) return;

    const allIds = this.quiz.questions;
    const batchSize = 10;
    const promises = [];

    // 1. Prepare all the "calls", but don't update state yet
    for (let i = 0; i < allIds.length; i += batchSize) {
      const batchIds = allIds.slice(i, i + batchSize);
      promises.push(this.quizService.getQuizQuestions(this.groupId, batchIds));
    }

    try {
      this.isWorking.set(true);

      // 2. Fire all requests in parallel and wait for all to return
      const results = await Promise.all(promises);

      // 3. Flatten the results (Array of Arrays -> Single Array)
      const flattenedQuestions = results.flat() as QuizQuestion[];

      // 4. Update the Signal ONCE
      this.selectedQuestions.set(flattenedQuestions);
    } catch (error) {
      this.logger.error('Error loading quiz questions:', error);
      this.utilsService.onShowAlert({
        code: 'LOAD_QUESTIONS_ERROR',
        title: 'Error',
        message: 'Failed to load all questions.',
      });
    } finally {
      this.isWorking.set(false);
    }
  }

  @HostListener('window:resize')
  checkScreenSize() {
    this.isMobile = window.innerWidth < 768;
  }

  toggleSearch() {
    this.showSearch = !this.showSearch;
  }

  get animationParams() {
    return {
      value: 'visible',
      params: {
        startPos: this.isMobile ? 'translateY(100%)' : 'translateX(100%)',
      },
    };
  }

  toggleQuestion(q: any, addOrRemoveFlag: boolean) {
    if (addOrRemoveFlag) {
      this.addQuestion(q);
    } else {
      this.removeQuestion(q);
    }
  }

  addQuestion(q: any) {
    if (!this.selectedQuestions().find((item) => item.id === q.id)) {
      this.selectedQuestions.update((questions) => [...questions, q]);
    }
  }

  removeQuestion(q: any) {
    this.selectedQuestions.update((questions) =>
      questions.filter((item) => item.id !== q.id),
    );
  }

  isQuestionSelected(questionId: string): boolean {
    return this.selectedQuestions().some((q) => q.id === questionId);
  }



  updateQuiz(draft: boolean = false) {
    if (
      this.quizForm.invalid ||
      !this.groupId ||
      !this.quiz?.id ||
      !this.user?.uid ||
      !this.user?.name
    ) {
      this.logger.error(
        'Missing required information to update quiz session. Group ID: ',
        this.groupId,
        'QuizId: ',
        this.quiz?.id,
        ' User ID: ',
        this.user?.uid,
        ' User Name: ',
        this.user?.name,
      );
      this.utilsService.onShowAlert(
        {
          code: 'ERR_USER_INFO_MISSING',
          title: 'Error Updating Quiz',
          message: 'Missing required information. Please try again later.',
        } as AppExceptionModel,
        false,
      );
      return;
    }

    this.logger.debug('Updating quiz with data: ', {
      ...this.quizForm.value,
      selectedQuestionIds: this.selectedQuestions().map((q) => q.id),
    });

    this.isWorking.set(true);
    const tempQuiz: Quiz = this.quiz ?? ({} as Quiz);
    tempQuiz.byInvite = this.quizForm.getRawValue().byInvite;
    tempQuiz.inviteCode =
      tempQuiz.inviteCode ?? UtilsService.createInviteCode();
    tempQuiz.questions = this.selectedQuestions().map((q) => q.id!);
    tempQuiz.title = this.quizForm.getRawValue().title;
    tempQuiz.groupId = this.quizForm.getRawValue().groupId;
    tempQuiz.quizStatus = draft ? QuizStatus.draft : QuizStatus.scheduled;
    tempQuiz.totalScore = this.totalScore();
    tempQuiz.enableTimeLimit = this.quizForm.getRawValue().enableTimeLimit === true || this.quizForm.getRawValue().enableTimeLimit === 'true';
    tempQuiz.totalDuration = tempQuiz.enableTimeLimit ? Number(this.quizForm.getRawValue().totalDuration) : 0;
    tempQuiz.scheduledAt = this.quizForm.getRawValue().startDate
      ? Timestamp.fromDate(new Date(this.quizForm.getRawValue().startDate))
      : Timestamp.now();
    tempQuiz.endDate = this.quizForm.getRawValue().endDate
      ? Timestamp.fromDate(new Date(this.quizForm.getRawValue().endDate))
      : Timestamp.fromDate(dayjs().add(3, 'day').toDate());
    tempQuiz.isLive = false;
    tempQuiz.createdById = this.quizForm.getRawValue().createdById;
    tempQuiz.createdByName = this.quizForm.getRawValue().createdByName;
    tempQuiz.enableScoring = this.quizForm.getRawValue().enableScoring === true || this.quizForm.getRawValue().enableScoring === 'true';
    tempQuiz.allowMultipleAttempts = this.quizForm.getRawValue().allowMultipleAttempts === true || this.quizForm.getRawValue().allowMultipleAttempts === 'true';
    tempQuiz.shortAnswerPercentage = Number(this.quizForm.getRawValue().shortAnswerPercentage);
    this.quizService
      .updateQuiz(this.quiz?.id, tempQuiz)
      .then((_) => {
        this.logger.debug('Quiz updated with ID: ', tempQuiz.id);
        this.router.navigate(['..'], {
          relativeTo: this.activatedRoute,
        });
      })
      .catch((error: any) => {
        this.logger.error('Error updating quiz session: ', error);
        this.utilsService.onShowAlert(
          {
            code: 'UPDATE_QUIZ_ERROR',
            title: 'Error Updating Quiz',
            message: 'Failed to update quiz. Please try again later.',
          } as AppExceptionModel,
          false,
        );
      })
      .finally(() => {
        this.isWorking.set(false);
      });
  }
}
